import React, {useState, useCallback, useRef, CSSProperties, useMemo, useEffect} from 'react';
import SliderStyles from './SliderItemPage.module.scss';
import Modal from "../Modal/Modal";
import {Position, SliderProps} from "../../entity/index.entity";
import {API_URL_PHOTO} from "../../constants";

const SliderItemPage: React.FC<SliderProps> = ({images, activeIndex, onSlideChange}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(activeIndex);
    const [isMagnified, setIsMagnified] = useState(false);
    const [position, setPosition] = useState<Position>({x: 0, y: 0});
    const containerRef = useRef<HTMLDivElement>(null);
    const [transition, setTransition] = useState(false);

    useEffect(() => {
        setCurrentImageIndex(activeIndex);
    }, [activeIndex]);

    useEffect(() => {
        if (onSlideChange) {
            onSlideChange(currentImageIndex);
        }
    }, [currentImageIndex, onSlideChange]);

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const nextSlide = useCallback(() => {
        setTransition(true);
        setTimeout(() => {
            setCurrentImageIndex((prevIndex) => {
                const newIndex = (prevIndex + 1) % images.length;
                return newIndex;
            });
            setTransition(false);
        }, 300);
    }, [images.length]);

    const prevSlide = useCallback(() => {
        setTransition(true);
        setTimeout(() => {
            setCurrentImageIndex((prevIndex) => {
                const newIndex = (prevIndex - 1 + images.length) % images.length;
                return newIndex;
            });
            setTransition(false);
        }, 300);
    }, [images.length]);

    const currentImageSrc = images[currentImageIndex];

    const toggleMagnify = () => {
        setIsMagnified((prev) => !prev);
        setPosition({x: 0, y: 0});
    };

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!isMagnified || !containerRef.current) return;

        const {clientX, clientY} = e;
        const containerRect = containerRef.current.getBoundingClientRect();

        const {left, top, width, height} = containerRect;

        const x = clientX - left;
        const y = clientY - top;

        const imgWidth = 1200;
        const imgHeight = 1200;

        const maxX = (imgWidth - width) / 2;
        const maxY = (imgHeight - height) / 2;

        const minX = -maxX;

        let newX = (x / width) * (imgWidth - width) - maxX;
        newX = Math.max(Math.min(newX, maxX), minX);

        const newY = Math.min(Math.max((y / height) * (imgHeight - height), -maxY), maxY);

        setPosition({x: -newX, y: -newY});
    }, [isMagnified]);

    return (
        <>
            <div className={SliderStyles.sliderWrapper}>
                <button
                    className={`${SliderStyles.arrow} ${SliderStyles.arrowLeft}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        prevSlide();
                    }}
                >
                    <img src="/leftArrow.svg" alt={'leftArrow'} className={SliderStyles.arrowLeftItem}/>
                </button>
                <div
                    className={SliderStyles.container}
                    ref={containerRef}
                    onClick={() => handleImageClick(currentImageIndex)}
                >
                    <img
                        src={API_URL_PHOTO + currentImageSrc}
                        alt="fullSize"
                        className={`${SliderStyles.sliderImage} ${transition ? SliderStyles.transition : ''}`}
                    />
                </div>
                <button
                    className={`${SliderStyles.arrow} ${SliderStyles.arrowRight}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        nextSlide();
                    }}
                >
                    <img src="/rightArrow.svg" alt={'rightArrow'} className={SliderStyles.arrowRightItem}/>
                </button>
            </div>
            {isModalOpen && (
                <Modal onClose={handleCloseModal}>
                    <div className={SliderStyles.sliderWrapper}>
                        <button
                            className={`${SliderStyles.arrow} ${SliderStyles.arrowLeft}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                prevSlide();
                            }}
                        >
                            <img src="/leftArrow.svg" alt={'leftArrow'}/>
                        </button>
                        <div
                            className={`${SliderStyles.containerModal} ${isMagnified ? SliderStyles.zoomed : ''}`}
                            onMouseMove={handleMouseMove}
                            ref={containerRef}
                            onClick={toggleMagnify}
                        >
                            <img
                                src={API_URL_PHOTO + currentImageSrc}
                                alt="fullSize"
                                className={`${SliderStyles.modalImage} ${transition ? SliderStyles.transition : ''}`}
                                style={{
                                    transform: isMagnified
                                        ? `translate(${position.x}px, ${position.y}px) translate(-50%, -50%)`
                                        : 'translate(-50%, -50%)',
                                }}
                            />
                        </div>
                        <button
                            className={`${SliderStyles.arrow} ${SliderStyles.arrowRight}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                nextSlide();
                            }}
                        >
                            <img src="/rightArrow.svg" alt={'rightArrow'}/>
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default SliderItemPage;